/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ElementRef, type OnDestroy, type OnInit, Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';

import { SmartAssistantStrService } from '../smart-assistant-str.service';
import { ArrowE, AUTO_PLAY_POLLING_TIME, MIN_INDEX, PER_LINE_WORDS, PER_LINE_WORDS_HEIGHT, RESTART_AUTOPLAY_TIME } from './git-container.const';

@Component({
    selector: 'app-smart-assistant-gif-container',
    templateUrl: './gif-container.component.html',
    styleUrls: ['./gif-container.component.scss'],
})
export class SmartAssistantGifContainerComponent implements OnInit, OnDestroy {
    /**
     * 是否展示小标题旁的返回箭头
     */
    @Input() showBackBtn = false;
    /**
     * 副标题
     */
    @Input() subTitle: string;
    /**
     * 图片前缀（将图片统一放入/assets/images/smart-assistant）
     * 英文需要加-en的后缀
     * 图片编号从1开始
     */
    @Input() imagePrefix: string;
    /**
     * 轮播图对应提示
     */
    @Input() gifTips: string[] = [];

    /**
     * 点击了返回按钮
     */
    @Output() back = new EventEmitter<void>();

    /**
     * 动图元素
     */
    @ViewChildren('gifEls') gifEls: QueryList<ElementRef>;

    /**
     * 轮播图当前页
     */
    currentIndex = MIN_INDEX;
    /**
     * 轮播图最大索引
     */
    gifMaxIndex: number;
    /**
     * 图片下方展示的提示
     */
    displayTip: string;
    /**
     * 轮播图左右箭头
     */
    ArrowE = ArrowE;
    /**
     * 轮播图最小索引
     */
    MIN_INDEX = MIN_INDEX;
    /**
     * 轮播图tip的最大高度（所有tip一样高，避免切换时产生跳变）
     */
    tipMaxHeight: string;
    /**
     * 自动播放定时器
     */
    playTimer$: NodeJS.Timeout;
    /**
     * 重新开始自动播放定时器
     */
    restartTimer$: NodeJS.Timeout;

    constructor(
        public str: SmartAssistantStrService,
    ) { }

    ngOnInit(): void {
        this.gifMaxIndex = this.gifTips.length - 1;
        this.displayTip = this.gifTips[0];
        this.tipMaxHeight = this.getTipMaxHeight();
        this.setAutoPlay();
    }

    /**
     * 获取动图tip的最大高度
     */
    getTipMaxHeight(): string {
        let maxHeight = 0;
        this.gifTips.forEach((tip: string) => {
            // 2个英文字符的宽度约等于1个汉字/日语字符的宽度
            const length = this.str.isEn ? Math.ceil(tip.length / 2) : tip.length;
            const tipHeight = Math.ceil(length / PER_LINE_WORDS) * PER_LINE_WORDS_HEIGHT;
            if (maxHeight < tipHeight) {
                maxHeight = tipHeight;
            }
        });

        return `${maxHeight}px`;
    }

    /**
     * 自动播放
     */
    setAutoPlay = (): void => {
        if (!this.playTimer$) {
            this.playTimer$ = setInterval(() => {
                this.gifBtnClick(ArrowE.right);
            }, AUTO_PLAY_POLLING_TIME);
        }
    };

    /**
     * 重新开始自动播放
     * 用户干预了自动播放，可能已经阅读完了图片和文字，因此这里缩短第一次切换时间
     */
    restartAutoPlay = (): void => {
        if (!this.restartTimer$) {
            this.restartTimer$ = setTimeout(() => {
                this.gifBtnClick(ArrowE.right);
                this.setAutoPlay();
                clearTimeout(this.restartTimer$);
                this.restartTimer$ = null;
            }, RESTART_AUTOPLAY_TIME);
        }
    };

    /**
     * 按钮被点击
     */
    clickBack(): void {
        this.back.emit();
    }

    /**
     * 轮播图切换
     */
    gifBtnClick(arrow: ArrowE): void {
        const lastIndex = this.currentIndex;
        if (arrow === ArrowE.left) {
            if (this.currentIndex === MIN_INDEX) {
                this.currentIndex = this.gifMaxIndex;
            } else {
                this.currentIndex--;
            }
            this.gifEls?.get(this.currentIndex).nativeElement.classList.add('img-back');
        }

        if (arrow === ArrowE.right) {
            if (this.currentIndex === this.gifMaxIndex) {
                this.currentIndex = MIN_INDEX;
            } else {
                this.currentIndex++;
            }
            this.gifEls?.get(this.currentIndex).nativeElement.classList.add('img-go');
        }

        // 将src路径重置，保证gif从第一帧开始播放
        if (this.gifEls) {
            this.gifEls.get(this.currentIndex).nativeElement.src = '';
            this.gifEls.get(this.currentIndex).nativeElement.src = this.getImageUrl(this.currentIndex);
        }
        this.displayTip = this.gifTips[this.currentIndex];

        // 移除上一个图片的“动画类”，使下次给图片增加“动画类”时，动画生效
        this.gifEls?.get(lastIndex).nativeElement.classList.remove('img-go');
        this.gifEls?.get(lastIndex).nativeElement.classList.remove('img-back');
    }

    /**
     * 获取轮播图背景图片地址
     */
    getImageUrl = (index: number): string => this.str.isZh ?
        `/assets/images/smart-assistant/${this.imagePrefix}-${index + 1}.gif` :
        `/assets/images/smart-assistant/${this.imagePrefix}-${index + 1}-en.gif`;

    /**
     * 停止自动播放
     */
    stopAutoPlay(): void {
        clearInterval(this.playTimer$);
        this.playTimer$ = null;
        clearTimeout(this.restartTimer$);
        this.restartTimer$ = null;
    }

    ngOnDestroy(): void {
        this.stopAutoPlay();
    }
}
